import React, { useEffect, useMemo, useState, useCallback } from "react";
import type { FC, CSSProperties } from "react";
import { Remarkable } from 'remarkable';
import examStyle from '../index.less';
import { Button, Input, message, Modal, Popover, Select } from "antd";
import type { OnChange, OnMount } from "@monaco-editor/react";
import Editor from "@monaco-editor/react";
import { judge } from "@/api/examinationApi";
import TextArea from "rc-textarea";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useLocation } from "umi";
type newCodeProblem = API.codeProblem & { reply: string; language: number };
interface Props { 
    problems: newCodeProblem[]; 
    onChange?: (value: newCodeProblem[]) => void; 
    className?: string; 
    style?: CSSProperties 
}

/** 处理 number，string，number[] 类型的转换 */
const typeToString = (value:number | string | number[]) => {
    const obj = {
        /** 整数处理 */
        number(val: number) {
            return String(val);
        },
        /** 字符串处理 */
        string(val: string) {
            if(val.includes(',')) {
                return `[${val}]`;
            }
            return val;
        },
        /** 数组处理 */
        numberObj(val: number[]) {
            return `[${val.join(',')}]`;
        }
    };
    if(Array.isArray(value)) {
        return obj.numberObj(value);
    } else {
        return obj[typeof value](value);
    }
};
const md = new Remarkable();

/** 编程题 */
const CodeList: FC<Props> = ({ problems, onChange, className, style }) => {
    const { query } = useLocation() as unknown as Location & { query: Record<string, string> };
    const [mdContent, setMdContent] = useState();
    const newProblems = useMemo(() => [...problems], [problems]);
    const [problemIndex, setProblemIndex] = useState(0);
    const [languageList, setLanguageList] = useState<{ label: string; value: number }[]>([]);
    const [selectLanguage, setSelectLanguage] = useState(problems[problemIndex].templateFunctions[0].languageId);
    const [codeRunLoading, setCodeRunLoading] = useState(false);
    const [customInput, setCustomInput] = useState(newProblems[problemIndex].testCaseInput[0]);
    
    useEffect(() => {
        judge.languages().then(res => {
            if (res.code === 0) {
                setLanguageList(res.data.map(item => ({
                    label: item.languageName,
                    value: item.languageId
                })));
            }
        });
    }, []);

    useEffect(() => {
        setMdContent(md.render(`## 使用说明 \n\n - 每道题将对一个特定类型的输入，经过多个步骤处理后，得到一个结果返回。考生需要将这些处理步骤整合在一起，写在一个 answer 方法里。answer 方法将作为判定得分的唯一标准，answer 方法中可以调用其他方法。 \n\n- 可以用**自测运行**来测试代码是否有问题，可以**自定义输入**，运行前请确保 answer 方法中的返回值**类型**无误，且 anwser 方法中，没有任何字符会被打印到 stdout 。某一语言的完整执行代码是 "前半部分" + 你的代码 + "后半部分"。 \n - 编辑器使用的是 monaco，键盘快捷键和默认的 vscode 是一样的 \n\n` + newProblems[problemIndex].content));
    }, [newProblems, problemIndex]);

    /** 下一题 */
    const nextProblem = () => {
        setProblemIndex((val) => {
            if (val + 1 < newProblems.length) {
                setSelectLanguage(newProblems[val + 1].language);
                setCustomInput(newProblems[val + 1].testCaseInput[0]);
                window.Pandora.send('zijin_testee_change_titleindex', {
                    onlyid: query.exam_id,
                    index: val + 2,
                    type: 'code'
                  });
                return val + 1;
            } else {
                return val;
            }
        });
    };
    /** 上一题 */
    const upProblem = () => {
        setProblemIndex((val) => {
            if (val === 0) {
                return 0;
            } else {
                setSelectLanguage(newProblems[val - 1].language);
                setCustomInput(newProblems[val - 1].testCaseInput[0]);
                window.Pandora.send('zijin_testee_change_titleindex', {
                    onlyid: query.exam_id,
                    index: val,
                    type: 'code'
                  });
                return val - 1;
            }
        });
    };

    /** 编辑器改变 */
    const handleEditorChange: OnChange = (value, ev) => {
        newProblems[problemIndex].reply = value || '';
        onChange?.(newProblems);
    };

    /** 编辑器加载完成 */
    const handleEditorOnMount: OnMount = (editor, monaco) => {
        // console.log(editor, monaco);
    };

    /** 处理选中的语言 */
    const getDealLanguage = useMemo(() => {
        const lv = languageList.find(item => item.value === selectLanguage)?.label || 'JavaScript (Node.js 12.14.0)';
        let l = lv.split(' ')[0].toLowerCase();
        if (l === 'c++') {
            l = 'cpp';
        } else if (l === 'c#') {
            l = 'csharp';
        }
        return l;
    }, [languageList, selectLanguage]);

    /** 获取当前题目的前后缀 */
    const getCurrentTitleRootPrefixSuffix = useMemo(() => {
        const targetObject = newProblems[problemIndex].templateFunctions.find(item => {
            if(item.languageId === selectLanguage) {
                return true;
            } else {
                return false;
            }
        });
        return [
            targetObject?.prefixTemplate ? md.render('```\n' + targetObject?.prefixTemplate + '\n```') : null, 
            targetObject?.suffixTemplate ? md.render('```\n' + targetObject?.suffixTemplate + '\n```') : null
        ];
    }, [problemIndex, newProblems, selectLanguage]);

    /** 切换语言 */
    const switchLanguage = (e: number) => {
        const language = newProblems[problemIndex].templateFunctions.find(item => item.languageId === e) || {
            languageId: 63,
            languageTemplate: "function answer(x){return x}"
        };
        newProblems[problemIndex].reply = language.languageTemplate;
        newProblems[problemIndex].language = language.languageId;
        window.Pandora.send('zijin_testee_change_language', {
            onlyid: query.exam_id,
            type: language.languageId
        });
        setSelectLanguage(e);
    };

    /** 执行代码 */
    const codeRun = () => {
        /** 轮询获取请求结果 */
        const polling = (val: string) => {
            setTimeout(() => {
                judge.submissionGet({ submissionId: val }).then(res => {
                    // 任务出错
                    if (res.code === 1) {
                        setCodeRunLoading(false);
                        Modal.error({
                            content: <div>
                                <div>--------</div>
                                <TextArea style={{width: '100%', marginTop: '10px'}} rows={6} value={res.data.errorInfo} />
                            </div>
                        });
                    } else if (res.code === 0) { // 任务完成
                        setCodeRunLoading(false);
                        const content = {
                            content: <div>
                                <div>--------</div>
                                <div>输入：{typeToString(customInput)}</div>
                                <div>输出：{res.data.actualOutput}</div>
                                <div>预期：{res.data.expectedOutput}</div>
                            </div>
                        };
                        if(res.data.actualOutput !== res.data.expectedOutput) {
                            Modal.error(content);
                        } else {
                            Modal.success(content);
                        }
                    } else {
                        polling(val);
                    }
                }).catch(e => {
                    setCodeRunLoading(false);
                    message.error('网络异常');
                });
            }, 2000);
        };
        const problem = newProblems[problemIndex];
        const obj = {
            /** 整数转换 */
            'number'(val: string):number | boolean {
                if(/^[0-9]+$/.test(val)) {
                    return Number(val);
                } else {
                    message.error('目前是整数类型：只能输入数字');
                    return false;
                }
            },
            /** 数组转换 */
            'number[]'(val: string | number | number[]):number[] | boolean {
                const newVal = String(val);
                if(/^[0-9,]+$/.test(newVal)) {
                    return newVal.split(',').map(item => Number(item));
                } else {
                    message.error('整数数组类型：只能输入数字和,');
                    return false;
                }
            }
        };
        const input = obj[problem.inputType](customInput);
        if(typeof input === 'boolean') {
            return;
        }
        setCodeRunLoading(true);
        judge.submission({
            problemId: problem.problemId,
            languageId: problem.language,
            func: problem.reply,
            input: input
        }).then(res => {
            window.Pandora.send('zijin_testee_click_run', {
                data: JSON.stringify({
                    problemId: problem.problemId,
                    languageId: problem.language,
                    func: problem.reply,
                    input: input
                }),
                onlyid: query.exam_id,
              });
            polling(res.data.submissionId);
        }).catch(() => {
            setCodeRunLoading(false);
        });
    };

    return (
        <div className={className} style={style}>
            <div style={{ padding: '10px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Button disabled={problemIndex === 0} type="link" onClick={upProblem}>上一题</Button>
                <span style={{ color: 'black' }}>{`${problemIndex + 1}/${newProblems.length}`}</span>
                <Button disabled={problemIndex + 1 === newProblems.length} type="link" style={{ marginRight: '10px' }} onClick={nextProblem}>下一题</Button>
                <Select style={{ width: '200px' }} value={selectLanguage} onChange={switchLanguage} options={languageList} />
                {newProblems[problemIndex].allowCustomInput && <div style={{display: "flex", justifyContent: 'center', alignItems: 'center', marginLeft: '10px' }}>
                    <span>自定义输入：</span>
                    <Input value={customInput} onChange={(e) => { setCustomInput(e.target.value) ; }} style={{width: '200px'}} />
                    <Popover content={<div><p>目前只有两种类型的输入：整数和整数数组</p><p>整数：123</p><p>整数数组：1,2,3</p></div>} title="自定义输入提示">
                        <QuestionCircleOutlined style={{color: '#1890ff', margin: '0 20px 0 10px'}} />
                    </Popover>
                </div>}
                <Button style={{ marginLeft: '10px' }} loading={codeRunLoading} type="primary" onClick={codeRun}>自测运行</Button>
            </div>
            <div className={examStyle['code-question']}>
                {/* md 显示区域 */}
                <div className={['markdown-body', examStyle.md].join(' ')}>
                    <div dangerouslySetInnerHTML={{ __html: mdContent || '' }} />
                </div>
                {/* 中间竖线 */}
                <div className={examStyle.pillar} />
                {/* 编辑区域 */}
                <div className={examStyle.editor}>
                    <Popover placement="bottomLeft" overlayStyle={{width: '45vw'}} content={<div className={examStyle.prefix} dangerouslySetInnerHTML={{ __html: getCurrentTitleRootPrefixSuffix[0] || '' }} />} >
                        {getCurrentTitleRootPrefixSuffix[0] && <Button type="default" style={{marginBottom: '10px'}}>前半部分</Button>}
                    </Popover>
                    <Editor
                        language={getDealLanguage}
                        height={`calc(100% - ${140 - (getCurrentTitleRootPrefixSuffix[0] ? 0 : 42) - (getCurrentTitleRootPrefixSuffix[1] ? 0 : 42)}px)`}
                        value={newProblems[problemIndex].reply}
                        onChange={handleEditorChange}
                        onMount={handleEditorOnMount}
                        options={{
                            contextmenu: false,// 关闭右键菜单
                            minimap: {
                                enabled: false, // 关闭小地图
                            },
                            wordWrap: 'on', // 控制文字折行
                            lineNumbers: false,
                        }}
                    />
                    <Popover placement="topLeft" overlayStyle={{width: '45vw'}} content={<div className={examStyle.suffix} dangerouslySetInnerHTML={{ __html: getCurrentTitleRootPrefixSuffix[1] || '' }} />} >
                        {getCurrentTitleRootPrefixSuffix[1] && <Button type="default" style={{marginTop: '10px'}}>后半部分</Button>}
                        {/* <Button type="default" style={{marginTop: '10px'}}>后半部分</Button> */}
                    </Popover>
                </div>
            </div>
        </div>
    );
};
export default CodeList;